<template>
  <div class="cart-container">
    <NavbarWorld @cart-click="handleCartRedirect" />
    
    <!-- HERO CON EFECTOS -->
    <section class="cart-hero">
      <div class="hero-background">
        <div class="floating-shapes">
          <div class="shape shape-1"></div>
          <div class="shape shape-2"></div>
          <div class="shape shape-3"></div>
          <div class="shape shape-4"></div>
        </div>
        <div class="hero-glow"></div>
      </div>
      <div class="hero-content">
        <div class="hero-icon">
          <i class="fas fa-shopping-cart"></i>
          <span class="pulse-ring"></span>
        </div>
        <h1 class="hero-title">
          <span class="text-gradient">Carrito de Compras</span>
        </h1>
        <p class="hero-subtitle">Revisa tus productos y completa tus datos para finalizar la compra</p>
        <div class="hero-stats" v-if="cartItems.length > 0">
          <span class="stat-item">
            <i class="fas fa-box"></i>
            {{ cartItems.length }} productos
          </span>
          <span class="stat-divider">|</span>
          <span class="stat-item">
            <i class="fas fa-dollar-sign"></i>
            {{ formatUSD(cartTotal) }}
          </span>
        </div>
      </div>
    </section>

    <main class="cart-main">
      <div class="container">
        <div class="cart-layout">
          <!-- SECCIÓN IZQUIERDA: PRODUCTOS -->
          <div class="cart-products-section" :class="{ 'empty': cartItems.length === 0 }">
            <div class="section-header">
              <div class="header-left">
                <h2>
                  <i class="fas fa-shopping-bag"></i>
                  Productos
                  <span class="badge" v-if="cartItems.length > 0">{{ cartItems.length }}</span>
                </h2>
              </div>
              <div class="header-actions">
                <button v-if="cartItems.length > 0" class="btn-clear-cart" @click="openClearModal">
                  <i class="fas fa-trash-alt"></i>
                  <span>Vaciar carrito</span>
                </button>
              </div>
            </div>

            <div v-if="cartItems.length === 0" class="empty-cart">
              <div class="empty-animation">
                <i class="fas fa-shopping-cart"></i>
                <div class="empty-pulse"></div>
              </div>
              <h3>Tu carrito está vacío</h3>
              <p>¡Explora nuestra tienda y encuentra los mejores productos!</p>
              <router-link to="/catalogo" class="btn-primary btn-glow">
                <i class="fas fa-store"></i>
                Ir a la tienda
              </router-link>
            </div>

            <div v-else class="cart-items">
              <div
                v-for="(item, index) in cartItems"
                :key="index"
                class="cart-item"
                :style="{ animationDelay: `${index * 0.05}s` }"
              >
                <div class="item-image-wrapper">
                  <div class="item-image">
                    <img 
                      :src="getProductImage(item.imagen)" 
                      :alt="item.nombre"
                      loading="lazy"
                      @error="handleImageError"
                    />
                    <div class="item-image-placeholder" v-if="!item.imagen || item.imagen === 'no-image'">
                      <i class="fas fa-image"></i>
                    </div>
                  </div>
                  <div class="item-badge" :class="item.paymentType">
                    <i :class="item.paymentType === 'credit' ? 'fas fa-credit-card' : 'fas fa-money-bill-wave'"></i>
                    {{ item.paymentType === 'credit' ? 'Crédito' : 'Contado' }}
                  </div>
                </div>

                <div class="item-details">
                  <div class="item-header">
                    <h3>{{ item.nombre }}</h3>
                    <button class="btn-remove" @click="openRemoveModal(index)" title="Eliminar">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <p class="item-code"><i class="fas fa-barcode"></i> {{ item.codigo || 'N/A' }}</p>
                  
                  <div class="item-price-row">
                    <div class="item-price">
                      <span class="price-label">Precio unitario:</span>
                      <span class="price-value">{{ formatUSD(item.precio) }}</span>
                      <span v-if="item.paymentType === 'credit'" class="price-original">
                        <i class="fas fa-tag"></i>
                        Contado: {{ formatUSD(item.precioOriginal) }}
                      </span>
                    </div>
                  </div>

                  <div class="item-quantity">
                    <button class="qty-btn" @click="updateQuantity(index, -1)" :disabled="item.quantity <= 1">
                      <i class="fas fa-minus"></i>
                    </button>
                    <span class="qty-value">{{ item.quantity }}</span>
                    <button class="qty-btn" @click="updateQuantity(index, 1)">
                      <i class="fas fa-plus"></i>
                    </button>
                    <span class="item-subtotal">
                      Subtotal: <strong>{{ formatUSD(item.precio * item.quantity) }}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Resumen del carrito -->
              <div class="cart-summary">
                <div class="summary-card">
                  <div class="summary-row">
                    <span><i class="fas fa-receipt"></i> Subtotal:</span>
                    <span>{{ formatUSD(cartSubtotal) }}</span>
                  </div>
                  <div class="summary-row highlight" v-if="hasCreditItems">
                    <span><i class="fas fa-percent"></i> Recargo por crédito:</span>
                    <span class="credit-surcharge">+{{ formatUSD(creditSurcharge) }}</span>
                  </div>
                  <div class="summary-row total">
                    <span><i class="fas fa-calculator"></i> Total:</span>
                    <span class="total-amount">{{ formatUSD(cartTotal) }}</span>
                  </div>
                  <div class="summary-row bs" v-if="showBs">
                    <span><i class="fas fa-bs"></i> Total en Bs:</span>
                    <span class="total-bs">{{ formatBs(cartTotal) }}</span>
                  </div>
                  <div class="summary-footer">
                    <i class="fas fa-check-circle"></i>
                    <span>Precios incluyen IVA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- SECCIÓN DERECHA: FORMULARIO CLIENTE -->
          <div class="customer-form-section" v-if="cartItems.length > 0">
            <div class="form-card">
              <div class="form-header">
                <div class="form-header-icon">
                  <i class="fas fa-user-circle"></i>
                </div>
                <h2>Datos del Cliente</h2>
                <p>Completa tus datos para finalizar la compra</p>
              </div>

              <form @submit.prevent="sendToWhatsApp" class="customer-form">
                <div class="form-group">
                  <label for="nombre">
                    <i class="fas fa-user"></i> Nombre y Apellido
                    <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    v-model="customerData.nombre"
                    placeholder="Ej: Juan Pérez"
                    required
                    :class="{ 'error': submitted && !customerData.nombre }"
                  />
                  <span class="error-message" v-if="submitted && !customerData.nombre">Este campo es obligatorio</span>
                </div>

                <div class="form-group">
                  <label for="cedula">
                    <i class="fas fa-id-card"></i> Número de Cédula
                    <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="cedula"
                    v-model="customerData.cedula"
                    placeholder="Ej: V-12345678"
                    required
                    :class="{ 'error': submitted && !customerData.cedula }"
                  />
                  <span class="error-message" v-if="submitted && !customerData.cedula">Este campo es obligatorio</span>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="telefono">
                      <i class="fas fa-phone"></i> Teléfono
                      <span class="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      v-model="customerData.telefono"
                      placeholder="0424-1234567"
                      required
                      :class="{ 'error': submitted && !customerData.telefono }"
                    />
                    <span class="error-message" v-if="submitted && !customerData.telefono">Este campo es obligatorio</span>
                  </div>

                  <div class="form-group">
                    <label for="email">
                      <i class="fas fa-envelope"></i> Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      v-model="customerData.email"
                      placeholder="ejemplo@correo.com"
                    />
                  </div>
                </div>

                <div class="form-group">
                  <label for="direccion">
                    <i class="fas fa-map-marker-alt"></i> Dirección
                    <span class="required">*</span>
                  </label>
                  <textarea
                    id="direccion"
                    v-model="customerData.direccion"
                    placeholder="Ej: Av. Principal, Edif. Central, Piso 2, Oficina 5"
                    rows="3"
                    required
                    :class="{ 'error': submitted && !customerData.direccion }"
                  ></textarea>
                  <span class="error-message" v-if="submitted && !customerData.direccion">Este campo es obligatorio</span>
                </div>

                <div class="form-group">
                  <label for="metodo-pago">
                    <i class="fas fa-credit-card"></i> Método de Pago
                    <span class="required">*</span>
                  </label>
                  <select id="metodo-pago" v-model="customerData.metodoPago" required>
                    <option value="">Selecciona un método de pago</option>
                    <option value="Efectivo">Efectivo (Bs/USD)</option>
                    <option value="Transferencia">Transferencia Bancaria</option>
                    <option value="Pago Móvil">Pago Móvil</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Pago en Tienda">Pago en Tienda</option>
                  </select>
                  <span class="error-message" v-if="submitted && !customerData.metodoPago">Selecciona un método de pago</span>
                </div>

                <!-- DATOS BANCARIOS PARA PAGO MÓVIL - SE MUESTRAN EN EL FORMULARIO -->
                <div v-if="customerData.metodoPago === 'Pago Móvil'" class="bank-data-card">
                  <div class="bank-data-header">
                    <i class="fas fa-university"></i>
                    <span>Datos para Pago Móvil</span>
                  </div>
                  <div class="bank-data-content">
                    <div class="bank-row">
                      <span class="bank-label">Banco:</span>
                      <span class="bank-value">Venezuela</span>
                    </div>
                    <div class="bank-row">
                      <span class="bank-label">Cédula:</span>
                      <span class="bank-value">V-18731281</span>
                    </div>
                    <div class="bank-row">
                      <span class="bank-label">Teléfono:</span>
                      <span class="bank-value">04167594723</span>
                    </div>
                    <div class="bank-row">
                      <span class="bank-label">Banco (Código):</span>
                      <span class="bank-value">0102</span>
                    </div>
                    <div class="bank-row highlight-bank">
                      <span class="bank-label">Monto a Pagar:</span>
                      <span class="bank-value amount">{{ formatBs(cartTotal) }}</span>
                    </div>
                  </div>
                </div>

                <!-- CAMPO PARA NÚMERO DE REFERENCIA (solo Pago Móvil) -->
                <div v-if="customerData.metodoPago === 'Pago Móvil'" class="form-group">
                  <label for="referencia">
                    <i class="fas fa-hashtag"></i> Número de Referencia
                    <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="referencia"
                    v-model="customerData.referencia"
                    placeholder="Ej: 1234567890"
                    required
                    :class="{ 'error': submitted && !customerData.referencia }"
                  />
                  <span class="error-message" v-if="submitted && !customerData.referencia">Ingresa el número de referencia</span>
                  <small class="help-text"><i class="fas fa-info-circle"></i> Número de referencia que te dio el banco al realizar el pago</small>
                </div>

                <!-- CAMPO PARA CAPTURE (solo Pago Móvil) -->
                <div v-if="customerData.metodoPago === 'Pago Móvil'" class="form-group">
                  <label for="capture">
                    <i class="fas fa-image"></i> Capture del Pago
                    <span class="required">*</span>
                  </label>
                  <div class="file-upload-wrapper">
                    <input
                      type="file"
                      id="capture"
                      accept="image/*"
                      @change="handleFileUpload"
                      required
                      :class="{ 'error': submitted && !customerData.capture }"
                    />
                    <div class="file-upload-placeholder" v-if="!customerData.capture">
                      <i class="fas fa-cloud-upload-alt"></i>
                      <span>Haz clic para subir la captura</span>
                      <small>Formatos: JPG, PNG, PDF</small>
                    </div>
                    <div class="file-upload-preview" v-else>
                      <i class="fas fa-check-circle"></i>
                      <span>{{ customerData.captureName || 'Captura subida' }}</span>
                      <button type="button" class="btn-remove-file" @click="removeFile">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <span class="error-message" v-if="submitted && !customerData.capture">Sube la captura del pago</span>
                  <small class="help-text"><i class="fas fa-info-circle"></i> Sube la captura de pantalla del pago realizado</small>
                </div>

                <div class="form-group">
                  <label for="observaciones">
                    <i class="fas fa-comment"></i> Observaciones
                  </label>
                  <textarea
                    id="observaciones"
                    v-model="customerData.observaciones"
                    placeholder="Ej: Entregar en horario de oficina, llamar antes de llegar..."
                    rows="2"
                  ></textarea>
                </div>

                <div class="form-actions">
                  <button type="submit" class="btn-whatsapp" :disabled="isSending">
                    <i class="fab fa-whatsapp"></i>
                    <span v-if="!isSending">Enviar pedido por WhatsApp</span>
                    <span v-else><i class="fas fa-spinner fa-spin"></i> Enviando...</span>
                  </button>
                  <button type="button" class="btn-continuar" @click="continueShopping">
                    <i class="fas fa-arrow-left"></i>
                    Seguir comprando
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>

    <FooterSection />

    <!-- MODAL DE CONFIRMACIÓN -->
    <div v-if="modalVisible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container" :class="modalType">
        <div class="modal-icon">
          <i :class="modalIcon"></i>
        </div>
        <h3 class="modal-title">{{ modalTitle }}</h3>
        <p class="modal-message">{{ modalMessage }}</p>
        <div class="modal-actions">
          <button v-if="modalShowCancel" class="btn-modal-cancel" @click="closeModal">
            Cancelar
          </button>
          <button class="btn-modal-confirm" :class="modalConfirmClass" @click="modalConfirmAction">
            <i :class="modalConfirmIcon"></i>
            {{ modalConfirmText }}
          </button>
        </div>
      </div>
    </div>

    <!-- TOAST NOTIFICATIONS -->
    <div class="toast-container">
      <div 
        v-for="(toast, index) in toasts" 
        :key="index"
        class="toast"
        :class="toast.type"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <div class="toast-icon">
          <i :class="toast.icon"></i>
        </div>
        <div class="toast-content">
          <span class="toast-message">{{ toast.message }}</span>
        </div>
        <button class="toast-close" @click="removeToast(index)">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import NavbarWorld from '@/components/NavbarWorld.vue';
import FooterSection from '@/components/FooterSection.vue';
import { obtenerUrlImagen } from '@/api/productos.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';

export default {
  name: 'CartWorld',
  components: { NavbarWorld, FooterSection },

  data() {
    return {
      cartItems: [],
      customerData: {
        nombre: '',
        cedula: '',
        telefono: '',
        email: '',
        direccion: '',
        metodoPago: '',
        referencia: '',
        capture: null,
        captureName: '',
        observaciones: ''
      },
      submitted: false,
      isSending: false,
      showBs: false,
      currentRate: null,
      whatsappNumber: '584125696219',
      // Modal
      modalVisible: false,
      modalType: 'warning',
      modalTitle: '',
      modalMessage: '',
      modalIcon: 'fas fa-exclamation-circle',
      modalShowCancel: true,
      modalConfirmText: 'Confirmar',
      modalConfirmClass: 'btn-confirm-warning',
      modalConfirmIcon: 'fas fa-check',
      modalConfirmAction: null,
      modalRemoveIndex: null,
      // Toasts
      toasts: []
    };
  },

  computed: {
    cartSubtotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.precio * item.quantity), 0);
    },
    hasCreditItems() {
      return this.cartItems.some(item => item.paymentType === 'credit');
    },
    creditSurcharge() {
      let surcharge = 0;
      this.cartItems.forEach(item => {
        if (item.paymentType === 'credit') {
          const originalTotal = item.precioOriginal * item.quantity;
          const creditTotal = item.precio * item.quantity;
          surcharge += creditTotal - originalTotal;
        }
      });
      return surcharge;
    },
    cartTotal() {
      return this.cartItems.reduce((sum, item) => sum + (item.precio * item.quantity), 0);
    },
    whatsappMessage() {
      let message = '';
      const now = new Date();
      const fecha = now.toLocaleDateString('es-VE');
      const hora = now.toLocaleTimeString('es-VE');
      
      message += 'NUEVO PEDIDO - JEAN INFORMATICO C.A\n';
      message += '\n';
      
      message += 'DATOS DEL CLIENTE\n';
      message += '---\n';
      message += `Cliente: ${this.customerData.nombre}\n`;
      message += `Cedula: ${this.customerData.cedula}\n`;
      message += `Telefono: ${this.customerData.telefono}\n`;
      message += `Email: ${this.customerData.email || 'No especificado'}\n`;
      message += `Direccion: ${this.customerData.direccion}\n`;
      message += `Metodo de Pago: ${this.customerData.metodoPago}\n`;
      if (this.customerData.observaciones) {
        message += `Observaciones: ${this.customerData.observaciones}\n`;
      }
      
      // Si el método de pago es Pago Móvil, mostrar datos bancarios y referencia
      if (this.customerData.metodoPago === 'Pago Móvil') {
        message += '\n';
        message += 'DATOS PARA PAGO MOVIL\n';
        message += '---\n';
        message += 'Banco: Venezuela\n';
        message += 'Cedula: V-18731281\n';
        message += 'Telefono: 04167594723\n';
        message += 'Banco: 0102\n';
        message += `Monto a Pagar: ${this.formatBsNumber(this.cartTotal)}\n`;
        message += `Numero de Referencia: ${this.customerData.referencia || 'No especificado'}\n`;
        message += 'Capture: Se adjunta captura del pago\n';
      }
      
      message += '\n';
      message += 'DETALLE DEL PEDIDO\n';
      message += '---\n';
      message += '\n';

      this.cartItems.forEach((item, index) => {
        message += `${index + 1}. ${item.nombre}\n`;
        message += `   Codigo: ${item.codigo || 'N/A'}\n`;
        message += `   Precio: $${item.precio.toFixed(2)}\n`;
        message += `   Cantidad: ${item.quantity}\n`;
        message += `   Subtotal: $${(item.precio * item.quantity).toFixed(2)}\n`;
        message += `   Tipo: ${item.paymentType === 'credit' ? 'Credito (+17%)' : 'Contado'}\n`;
        if (item.paymentType === 'credit') {
          message += `   Precio Contado: $${item.precioOriginal.toFixed(2)}\n`;
        }
        message += '\n';
      });

      message += '---\n';
      message += `SUBTOTAL: $${this.cartSubtotal.toFixed(2)}\n`;
      if (this.creditSurcharge > 0) {
        message += `RECARGO (+17%): $${this.creditSurcharge.toFixed(2)}\n`;
      }
      message += `TOTAL: $${this.cartTotal.toFixed(2)}\n`;
      
      if (this.showBs && this.currentRate) {
        const totalBs = this.cartTotal * this.currentRate.precio_usd_bs;
        message += `TOTAL EN BS: Bs ${totalBs.toFixed(2)}\n`;
        message += `TASA BCV: ${this.currentRate.precio_usd_bs.toFixed(2)} Bs/USD\n`;
      }
      
      message += '---\n';
      message += `Fecha: ${fecha}\n`;
      message += `Hora: ${hora}\n`;
      message += 'Gracias por su compra!';
      
      return encodeURIComponent(message);
    }
  },

  watch: {
    cartItems: {
      handler(newItems) {
        localStorage.setItem('cart', JSON.stringify(newItems));
      },
      deep: true
    },
    'customerData.metodoPago': {
      handler(newVal) {
        // Limpiar campos de Pago Móvil cuando cambia el método
        if (newVal !== 'Pago Móvil') {
          this.customerData.referencia = '';
          this.customerData.capture = null;
          this.customerData.captureName = '';
        }
      }
    }
  },

  async mounted() {
    this.loadCart();
    await this.loadCurrentRate();
    const savedCustomer = localStorage.getItem('customerData');
    if (savedCustomer) {
      try {
        this.customerData = JSON.parse(savedCustomer);
      } catch (e) {
        console.warn('Error cargando datos del cliente:', e);
      }
    }
  },

  methods: {
    loadCart() {
      try {
        const cart = localStorage.getItem('cart');
        if (cart) {
          this.cartItems = JSON.parse(cart);
        }
      } catch (error) {
        console.error('Error cargando carrito:', error);
        this.cartItems = [];
      }
    },
    async loadCurrentRate() {
      try {
        const response = await obtenerTasaVentaActual();
        if (response.success) {
          this.currentRate = response.data;
          this.showBs = true;
        }
      } catch (error) {
        console.error('Error cargando tasa BCV:', error);
      }
    },
    getProductImage(img) {
      try {
        if (!img || img === 'no-image') {
          return null;
        }
        return obtenerUrlImagen(img);
      } catch {
        return null;
      }
    },
    handleImageError(event) {
      event.target.style.display = 'none';
      const container = event.target.closest('.item-image');
      if (container) {
        const placeholder = container.querySelector('.item-image-placeholder');
        if (placeholder) {
          placeholder.style.display = 'flex';
        }
      }
    },
    formatUSD(value) {
      const n = Number(value || 0);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n);
    },
    formatBs(value) {
      if (!this.currentRate) return 'Bs 0,00';
      const n = Number(value || 0);
      const bsAmount = n * this.currentRate.precio_usd_bs;
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VES',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(bsAmount).replace('VES', 'Bs');
    },
    formatBsNumber(value) {
      if (!this.currentRate) return '0,00';
      const n = Number(value || 0);
      const bsAmount = n * this.currentRate.precio_usd_bs;
      return bsAmount.toFixed(2).replace('.', ',');
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.customerData.capture = file;
        this.customerData.captureName = file.name;
        // Mostrar preview de la imagen
        const reader = new FileReader();
        reader.onload = (e) => {
          const preview = document.querySelector('.file-upload-preview');
          if (preview) {
            preview.style.backgroundImage = `url(${e.target.result})`;
            preview.style.backgroundSize = 'cover';
            preview.style.backgroundPosition = 'center';
          }
        };
        reader.readAsDataURL(file);
      }
    },
    removeFile() {
      this.customerData.capture = null;
      this.customerData.captureName = '';
      const input = document.getElementById('capture');
      if (input) input.value = '';
      const preview = document.querySelector('.file-upload-preview');
      if (preview) {
        preview.style.backgroundImage = '';
      }
    },
    updateQuantity(index, change) {
      const item = this.cartItems[index];
      const newQuantity = item.quantity + change;
      if (newQuantity <= 0) {
        this.openRemoveModal(index);
      } else {
        item.quantity = newQuantity;
        this.cartItems = [...this.cartItems];
      }
    },
    openRemoveModal(index) {
      this.modalRemoveIndex = index;
      this.modalType = 'danger';
      this.modalIcon = 'fas fa-trash-alt';
      this.modalTitle = '¿Eliminar producto?';
      this.modalMessage = '¿Estás seguro de eliminar este producto del carrito? Esta acción no se puede deshacer.';
      this.modalShowCancel = true;
      this.modalConfirmText = 'Eliminar';
      this.modalConfirmClass = 'btn-confirm-danger';
      this.modalConfirmIcon = 'fas fa-trash';
      this.modalConfirmAction = this.confirmRemove;
      this.modalVisible = true;
    },
    confirmRemove() {
      if (this.modalRemoveIndex !== null) {
        const itemName = this.cartItems[this.modalRemoveIndex]?.nombre || 'Producto';
        this.cartItems.splice(this.modalRemoveIndex, 1);
        this.modalRemoveIndex = null;
        this.closeModal();
        this.showToast('success', `"${itemName}" eliminado del carrito`);
      }
    },
    openClearModal() {
      this.modalType = 'warning';
      this.modalIcon = 'fas fa-exclamation-triangle';
      this.modalTitle = '¿Vaciar carrito?';
      this.modalMessage = 'Todos los productos serán eliminados del carrito. ¿Estás seguro de continuar?';
      this.modalShowCancel = true;
      this.modalConfirmText = 'Vaciar carrito';
      this.modalConfirmClass = 'btn-confirm-warning';
      this.modalConfirmIcon = 'fas fa-trash-alt';
      this.modalConfirmAction = this.confirmClear;
      this.modalVisible = true;
    },
    confirmClear() {
      this.cartItems = [];
      localStorage.removeItem('cart');
      this.closeModal();
      this.showToast('info', 'Carrito vaciado correctamente');
    },
    closeModal() {
      this.modalVisible = false;
      this.modalConfirmAction = null;
      this.modalRemoveIndex = null;
    },
    continueShopping() {
      this.$router.push('/catalogo');
    },
    validateForm() {
      this.submitted = true;
      const required = ['nombre', 'cedula', 'telefono', 'direccion', 'metodoPago'];
      let isValid = true;
      
      for (const field of required) {
        if (!this.customerData[field] || this.customerData[field].trim() === '') {
          isValid = false;
          break;
        }
      }
      
      // Validar campos de Pago Móvil
      if (this.customerData.metodoPago === 'Pago Móvil') {
        if (!this.customerData.referencia || this.customerData.referencia.trim() === '') {
          isValid = false;
        }
        if (!this.customerData.capture) {
          isValid = false;
        }
      }
      
      return isValid;
    },
    // LIMPIAR FORMULARIO DESPUÉS DE ENVIAR
    clearForm() {
      this.customerData = {
        nombre: '',
        cedula: '',
        telefono: '',
        email: '',
        direccion: '',
        metodoPago: '',
        referencia: '',
        capture: null,
        captureName: '',
        observaciones: ''
      };
      this.submitted = false;
      // Limpiar input file
      const input = document.getElementById('capture');
      if (input) input.value = '';
      // Eliminar datos guardados del cliente
      localStorage.removeItem('customerData');
    },
    async sendToWhatsApp() {
      if (!this.validateForm()) {
        this.showToast('error', 'Por favor completa todos los campos obligatorios');
        const firstError = document.querySelector('.form-group .error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (this.cartItems.length === 0) {
        this.showToast('error', 'El carrito está vacío');
        return;
      }
      this.isSending = true;
      try {
        localStorage.setItem('customerData', JSON.stringify(this.customerData));
        const phoneNumber = this.whatsappNumber;
        const message = this.whatsappMessage;
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
        window.open(whatsappUrl, '_blank');
        this.showToast('success', 'Pedido enviado a WhatsApp exitosamente');
        
        // LIMPIAR FORMULARIO DESPUÉS DE ENVIAR
        this.clearForm();
        
        // Opcional: También limpiar el carrito después de enviar
        // this.cartItems = [];
        // localStorage.removeItem('cart');
        
      } catch (error) {
        console.error('Error enviando a WhatsApp:', error);
        this.showToast('error', 'Error al enviar el pedido. Intenta nuevamente.');
      } finally {
        this.isSending = false;
      }
    },
    // Sistema de Toasts
    showToast(type, message) {
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        info: 'fas fa-info-circle',
        warning: 'fas fa-exclamation-triangle'
      };
      this.toasts.push({
        type,
        message,
        icon: icons[type] || icons.info
      });
      setTimeout(() => {
        if (this.toasts.length > 0) {
          this.toasts.shift();
        }
      }, 4000);
    },
    removeToast(index) {
      this.toasts.splice(index, 1);
    },
    handleCartRedirect() {
      this.continueShopping();
    }
  }
};
</script>

<style scoped>
/* ===== BASE ===== */
.cart-container {
  min-height: 100vh;
  background-color: #030812;
  background-image:
    radial-gradient(circle at 50% 12%, #0a1c2e 0%, #030812 75%),
    linear-gradient(rgba(0, 229, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 229, 255, 0.02) 1px, transparent 1px);
  background-size: 100% 100%, 45px 45px, 45px 45px;
  font-family: 'Poppins', sans-serif;
  width: 100%;
  overflow-x: hidden;
  color: #f8fafc;
}

.container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

@media (min-width: 640px) { .container { padding: 0 1.5rem; } }
@media (min-width: 1024px) { .container { padding: 0 2rem; } }

/* ===== HERO MEJORADO ===== */
.cart-hero {
  position: relative;
  padding: 7rem 1rem 2.5rem 1rem;
  text-align: center;
  overflow: hidden;
  background: radial-gradient(circle at 50% 40%, #0a1c2e 0%, #030812 85%);
  border-bottom: 1px solid rgba(0, 229, 255, 0.15);
}

@media (min-width: 768px) {
  .cart-hero { padding: 8rem 1rem 3rem 1rem; }
}

@media (min-width: 1024px) {
  .cart-hero { padding: 9.5rem 1rem 3.5rem 1rem; }
}

.hero-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
}

.hero-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(ellipse at center, rgba(0, 229, 255, 0.12) 0%, transparent 70%);
  animation: glowPulse 4s ease-in-out infinite;
}

@keyframes glowPulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
}

.floating-shapes {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  background: linear-gradient(135deg, #60a5fa, #4ade80);
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
  animation: floatShape 20s ease-in-out infinite;
}

.shape-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  left: -50px;
  animation: floatShape 25s ease-in-out infinite reverse;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.shape-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 20%;
  animation: floatShape 18s ease-in-out infinite 2s;
  background: linear-gradient(135deg, #a78bfa, #7c3aed);
}

.shape-4 {
  width: 100px;
  height: 100px;
  bottom: 30%;
  right: 15%;
  animation: floatShape 22s ease-in-out infinite 4s;
  background: linear-gradient(135deg, #34d399, #059669);
}

@keyframes floatShape {
  0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
  33% { transform: translate(30px, -20px) rotate(120deg) scale(1.1); }
  66% { transform: translate(-20px, 15px) rotate(240deg) scale(0.9); }
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 900px;
  margin: 0 auto;
}

.hero-icon {
  position: relative;
  display: inline-block;
  font-size: 3.5rem;
  color: #00e5ff;
  margin-bottom: 1rem;
  animation: bounceIn 0.8s ease;
}

@keyframes bounceIn {
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); }
}

.pulse-ring {
  position: absolute;
  inset: -15px;
  border: 2px solid rgba(0, 229, 255, 0.35);
  border-radius: 50%;
  animation: pulseRing 2s ease-out infinite;
}

@keyframes pulseRing {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.hero-title {
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

@media (min-width: 768px) {
  .hero-title { font-size: 3rem; }
}

.text-gradient {
  background: linear-gradient(135deg, #ffffff 0%, #7ef0ff 50%, #00e5ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
}

@keyframes gradientMove {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-subtitle {
  color: #94a3b8;
  font-size: 1rem;
  max-width: 600px;
  margin: 0 auto 1.5rem;
}

@media (min-width: 768px) {
  .hero-subtitle { font-size: 1.15rem; }
}

.hero-stats {
  display: inline-flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 229, 255, 0.25);
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  color: #7ef0ff;
  font-size: 0.9rem;
}

@media (min-width: 768px) {
  .hero-stats { font-size: 1rem; padding: 0.8rem 2rem; }
}

.stat-item i {
  margin-right: 0.4rem;
  color: #00e5ff;
}

.stat-divider {
  color: rgba(255,255,255,0.2);
}

/* ===== MAIN LAYOUT ===== */
.cart-main {
  padding: 1.5rem 0;
}

@media (min-width: 768px) {
  .cart-main { padding: 2.5rem 0; }
}

.cart-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 1024px) {
  .cart-layout {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 2rem;
  }
}

/* ===== PRODUCTS SECTION ===== */
.cart-products-section {
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(0, 229, 255, 0.2);
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .cart-products-section { padding: 2rem; }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.8rem;
}

.header-left h2 {
  font-size: 1.2rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 700;
}

@media (min-width: 768px) {
  .header-left h2 { font-size: 1.5rem; }
}

.header-left h2 i {
  color: #00e5ff;
}

.badge {
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  color: #030812;
  padding: 0.1rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-clear-cart {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.btn-clear-cart:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  transform: scale(1.03);
}

/* ===== EMPTY CART ===== */
.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-animation {
  position: relative;
  font-size: 5rem;
  color: #334155;
  margin-bottom: 1.5rem;
}

.empty-pulse {
  position: absolute;
  inset: -20px;
  border: 2px solid rgba(0, 229, 255, 0.2);
  border-radius: 50%;
  animation: pulseRing 2s ease-out infinite;
}

.empty-cart h3 {
  color: #ffffff;
  margin-bottom: 0.5rem;
  font-size: 1.3rem;
  font-weight: 700;
}

.empty-cart p {
  color: #94a3b8;
  margin-bottom: 1.5rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  color: #030812;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 229, 255, 0.4);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 0 25px rgba(0, 229, 255, 0.7);
}

/* ===== CART ITEMS ===== */
.cart-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cart-item {
  display: flex;
  gap: 1.2rem;
  padding: 1.2rem;
  background: rgba(11, 25, 46, 0.7);
  border-radius: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 229, 255, 0.15);
  animation: slideUp 0.4s ease forwards;
  opacity: 0;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.cart-item:hover {
  border-color: #00e5ff;
  box-shadow: 0 8px 30px rgba(0, 229, 255, 0.2);
  transform: translateY(-2px);
}

@media (max-width: 480px) {
  .cart-item {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}

.item-image-wrapper {
  position: relative;
  flex-shrink: 0;
}

.item-image {
  width: 90px;
  height: 90px;
  background: rgba(2, 6, 23, 0.8);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(0, 229, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

@media (min-width: 768px) {
  .item-image { width: 110px; height: 110px; }
}

.item-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.item-image-placeholder {
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f1f5f9;
  color: #94a3b8;
}

.item-image-placeholder i {
  font-size: 2.5rem;
}

.item-badge {
  position: absolute;
  bottom: -6px;
  right: -6px;
  font-size: 0.6rem;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.item-badge.cash {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.item-badge.credit {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.item-details {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.item-header h3 {
  font-size: 0.95rem;
  color: #ffffff;
  margin: 0;
  flex: 1;
  font-weight: 600;
}

@media (min-width: 768px) {
  .item-header h3 { font-size: 1.1rem; }
}

.btn-remove {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.3rem 0.5rem;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  border-radius: 8px;
}

.btn-remove:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
  transform: scale(1.1);
}

.item-code {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0.2rem 0;
}

.item-code i {
  margin-right: 0.3rem;
}

.item-price-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.3rem 0;
}

.item-price {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.price-label {
  font-size: 0.8rem;
  color: #94a3b8;
}

.price-value {
  font-weight: 700;
  color: #10b981;
  font-size: 1rem;
}

.price-original {
  color: #94a3b8;
  font-size: 0.75rem;
}

.price-original i {
  margin-right: 0.2rem;
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(0, 229, 255, 0.3);
  background: rgba(15, 23, 42, 0.85);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 0.8rem;
  color: #7ef0ff;
}

.qty-btn:hover:not(:disabled) {
  background: #00e5ff;
  color: #030812;
  border-color: #00e5ff;
  box-shadow: 0 0 12px rgba(0, 229, 255, 0.45);
  transform: scale(1.05);
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-value {
  font-weight: 700;
  color: #ffffff;
  min-width: 28px;
  text-align: center;
  font-size: 1rem;
}

.item-subtotal {
  font-size: 0.85rem;
  color: #94a3b8;
  margin-left: 0.3rem;
}

.item-subtotal strong {
  color: #7ef0ff;
}

/* ===== CART SUMMARY ===== */
.cart-summary {
  margin-top: 1.5rem;
}

.summary-card {
  background: rgba(11, 25, 46, 0.85);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 229, 255, 0.25);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: 0.95rem;
  color: #cbd5e1;
}

.summary-row.highlight {
  color: #7c3aed;
  font-weight: 500;
}

.summary-row.total {
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffffff;
  border-top: 1px solid rgba(0, 229, 255, 0.25);
  margin-top: 0.5rem;
  padding-top: 0.8rem;
}

.summary-row.bs {
  color: #00e5ff;
  font-weight: 600;
}

.summary-row i {
  margin-right: 0.4rem;
}

.total-amount {
  color: #10b981;
  font-size: 1.3rem;
  font-weight: 700;
}

.total-bs {
  color: #00e5ff;
  font-size: 1.1rem;
}

.credit-surcharge {
  color: #8b5cf6;
  font-weight: 600;
}

.summary-footer {
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.summary-footer i {
  color: #10b981;
}

/* ===== CUSTOMER FORM ===== */
.customer-form-section {
  position: sticky;
  top: 2rem;
  align-self: start;
}

.form-card {
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(0, 229, 255, 0.2);
}

@media (min-width: 768px) {
  .form-card { padding: 2rem; }
}

.form-header {
  text-align: center;
  margin-bottom: 1.8rem;
}

.form-header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00e5ff 0%, #1a73e8 100%);
  color: #030812;
  font-size: 1.8rem;
  margin-bottom: 0.8rem;
  box-shadow: 0 0 20px rgba(0, 229, 255, 0.4);
}

.form-header h2 {
  font-size: 1.3rem;
  color: #ffffff;
  margin: 0;
  font-weight: 700;
}

@media (min-width: 768px) {
  .form-header h2 { font-size: 1.5rem; }
}

.form-header p {
  color: #94a3b8;
  font-size: 0.85rem;
  margin-top: 0.2rem;
}

.customer-form {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.form-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #cbd5e1;
}

.form-group label i {
  color: #00e5ff;
  width: 18px;
}

.required {
  color: #ef4444;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.7rem 0.9rem;
  border: 1.5px solid rgba(0, 229, 255, 0.25);
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: rgba(11, 25, 46, 0.85);
  color: #ffffff;
  font-family: inherit;
  width: 100%;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #00e5ff;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.35);
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #64748b;
}

.form-group select option {
  background: #0f172a;
  color: #ffffff;
}

.form-group input.error,
.form-group textarea.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.error-message {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.2rem;
}

.help-text {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.2rem;
}

.help-text i {
  margin-right: 0.2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 480px) {
  .form-row { grid-template-columns: 1fr; }
}

/* ===== BANK DATA CARD ===== */
.bank-data-card {
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid rgba(16, 185, 129, 0.4);
  border-radius: 16px;
  padding: 1.2rem;
  animation: fadeIn 0.3s ease;
}

.bank-data-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1rem;
  font-weight: 700;
  color: #34d399;
  margin-bottom: 0.8rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(16, 185, 129, 0.2);
}

.bank-data-header i {
  font-size: 1.3rem;
}

.bank-data-content {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.bank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0;
  font-size: 0.9rem;
}

.bank-label {
  color: #a7f3d0;
  font-weight: 600;
}

.bank-value {
  color: #ffffff;
  font-weight: 500;
}

.bank-row.highlight-bank {
  background: rgba(16, 185, 129, 0.1);
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  margin-top: 0.2rem;
}

.bank-value.amount {
  color: #059669;
  font-weight: 700;
  font-size: 1.1rem;
}

/* ===== FILE UPLOAD ===== */
.file-upload-wrapper {
  position: relative;
  border: 2px dashed rgba(0, 229, 255, 0.35);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(11, 25, 46, 0.6);
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-wrapper:hover {
  border-color: #00e5ff;
  background: rgba(0, 229, 255, 0.08);
}

.file-upload-wrapper input[type="file"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.file-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  color: #94a3b8;
}

.file-upload-placeholder i {
  font-size: 2.5rem;
  color: #00e5ff;
}

.file-upload-placeholder span {
  font-size: 0.9rem;
  font-weight: 500;
}

.file-upload-placeholder small {
  font-size: 0.75rem;
  color: #94a3b8;
}

.file-upload-preview {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.6rem 1rem;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(0, 229, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
  width: 100%;
  min-height: 60px;
  position: relative;
  background-size: cover;
  background-position: center;
}

.file-upload-preview i {
  color: #10b981;
  font-size: 1.5rem;
}

.file-upload-preview span {
  flex: 1;
  font-size: 0.85rem;
  color: #ffffff;
  word-break: break-all;
}

.btn-remove-file {
  background: #fef2f2;
  border: none;
  color: #ef4444;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-remove-file:hover {
  background: #fee2e2;
  transform: scale(1.1);
}

.file-upload-wrapper.error {
  border-color: #ef4444;
  background: #fef2f2;
}

/* ===== FORM ACTIONS ===== */
.form-actions {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 0.5rem;
}

@media (min-width: 768px) {
  .form-actions { flex-direction: row; }
}

.btn-whatsapp {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 0.9rem 1.5rem;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 2;
}

.btn-whatsapp:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.35);
}

.btn-whatsapp:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-whatsapp i {
  font-size: 1.4rem;
}

.btn-continuar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 1.5rem;
  border: 1.5px solid rgba(0, 229, 255, 0.3);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.8);
  color: #7ef0ff;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}

.btn-continuar:hover {
  border-color: #00e5ff;
  background: rgba(0, 229, 255, 0.15);
  color: #00e5ff;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.35);
  transform: translateY(-3px);
}

/* ===== MODAL ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: #0b192e;
  border: 1.5px solid rgba(0, 229, 255, 0.35);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85);
  color: #ffffff;
  border-radius: 28px;
  padding: 2rem 2.5rem;
  max-width: 480px;
  width: 100%;
  text-align: center;
  animation: modalSlideUp 0.4s ease;
}

@keyframes modalSlideUp {
  from { transform: translateY(30px) scale(0.95); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}

.modal-container.danger .modal-icon {
  background: linear-gradient(135deg, #fef2f2, #fee2e2);
  color: #dc2626;
}

.modal-container.warning .modal-icon {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  color: #d97706;
}

.modal-container.success .modal-icon {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #059669;
}

.modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  font-size: 2.2rem;
  margin-bottom: 1.2rem;
}

.modal-title {
  font-size: 1.4rem;
  color: #ffffff;
  margin: 0 0 0.5rem;
}

.modal-message {
  color: #cbd5e1;
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
  line-height: 1.6;
}

.modal-actions {
  display: flex;
  gap: 0.8rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-modal-cancel {
  padding: 0.7rem 1.8rem;
  border: 1.5px solid rgba(0, 229, 255, 0.3);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.8);
  color: #cbd5e1;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-modal-cancel:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.btn-modal-confirm {
  padding: 0.7rem 1.8rem;
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-modal-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.btn-confirm-danger {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.btn-confirm-warning {
  background: linear-gradient(135deg, #d97706, #b45309);
}

.btn-confirm-success {
  background: linear-gradient(135deg, #059669, #047857);
}

/* ===== TOAST ===== */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-width: 420px;
  width: 100%;
  pointer-events: none;
}

@media (max-width: 480px) {
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
}

.toast {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 1.2rem;
  border-radius: 16px;
  background: white;
  box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  pointer-events: all;
  animation: toastSlideIn 0.4s ease;
  border-left: 4px solid;
}

@keyframes toastSlideIn {
  from { transform: translateX(100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.toast.success {
  border-color: #10b981;
}

.toast.success .toast-icon {
  color: #10b981;
}

.toast.error {
  border-color: #ef4444;
}

.toast.error .toast-icon {
  color: #ef4444;
}

.toast.info {
  border-color: #3b82f6;
}

.toast.info .toast-icon {
  color: #3b82f6;
}

.toast.warning {
  border-color: #f59e0b;
}

.toast.warning .toast-icon {
  color: #f59e0b;
}

.toast-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.toast-content {
  flex: 1;
}

.toast-message {
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 500;
}

.toast-close {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.2rem;
  font-size: 0.9rem;
  transition: color 0.2s ease;
}

.toast-close:hover {
  color: #475569;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 480px) {
  .cart-item { padding: 1rem; }
  .item-header h3 { font-size: 0.85rem; }
  .price-value { font-size: 0.9rem; }
  .btn-whatsapp, .btn-continuar { font-size: 0.85rem; padding: 0.7rem 1rem; }
  .modal-container { padding: 1.5rem; }
  .bank-data-card { padding: 1rem; }
  .bank-row { font-size: 0.8rem; flex-direction: column; align-items: flex-start; gap: 0.2rem; }
  .file-upload-wrapper { padding: 1rem; min-height: 80px; }
}
</style>